import { Component, computed, ElementRef, input, signal, TemplateRef, viewChild } from '@angular/core';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ALL_OPTION, FilterOption } from '../../model/filter-options.model';
import { EscapeTextPipe } from '../../pipes/escape-text.pipe';

@Component({
  selector: 'lib-ikp-dropdown',
  imports: [CommonModule, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, TranslatePipe, NgbDropdownItem, EscapeTextPipe],
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DropdownComponent,
      multi: true,
    },
  ],
})
export class DropdownComponent<T> implements ControlValueAccessor {
  readonly inputId = input.required<string>();
  readonly labelKey = input.required<string>();
  readonly disabled = input<boolean>(false);
  readonly options = input<FilterOption<T>[]>([]);
  readonly dropdownToggleType = input<'button' | 'input'>('button');
  readonly withDefaultOption = input<'all' | 'first' | 'none'>('none');
  readonly withAutoSelection = input<boolean>(true);
  readonly dataTestAttribute = input<{ page: string; scope: string }>();
  readonly optionTemplate = input<TemplateRef<{ $implicit: FilterOption<T> }>>();
  readonly customDropdownMenuClass = input<string>();

  protected readonly isInputDropdown = computed(() => this.dropdownToggleType() === 'input');
  protected readonly isDefaultOptionAll = computed(() => this.withDefaultOption() === 'all');
  protected readonly isDefaultOptionFirst = computed(() => this.withDefaultOption() === 'first');
  protected readonly defaultOption = computed(() => this.computeDefaultOption());
  protected readonly selectedOption = computed(() => this.computeSelectedOption());
  protected readonly filteredOptions = computed(() => this.computeFilteredOptions());
  protected readonly optionsList = computed(() => this.computeOptionsList());

  protected readonly inputValue = signal<string | null>(null);
  protected readonly ALL_OPTION = ALL_OPTION;

  private readonly dropdownRef = viewChild<NgbDropdown>('dropdownRef');
  private readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('inputRef');
  private readonly buttonRef = viewChild<ElementRef<HTMLButtonElement>>('buttonRef');
  private readonly storedSelectedOption = signal<FilterOption<T> | null>(null);

  protected onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dropdownRef()?.open();
    this.inputValue.set(value);
    this.checkAutoSelection();
  }

  protected selectOption(option: FilterOption<T>): void {
    this.storedSelectedOption.set(option);
    this.inputValue.set(option.text);
    this.onChange(option);
    this.handleFocus();
  }

  protected onDropdownOpenChange(isOpen: boolean): void {
    if (!isOpen) {
      this.onTouched();
      this.inputValue.set(this.storedSelectedOption()?.text || this.defaultOption()?.text || '');
    }
  }

  protected handleTabNavigation(event: KeyboardEvent): void {
    if (event.key == 'Tab') {
      this.dropdownRef()?.close();
      if (this.isInputDropdown()) {
        this.inputValue.set(this.storedSelectedOption()?.text || this.defaultOption()?.text || '');
      }
    }
  }

  private checkAutoSelection(): void {
    if (!this.withAutoSelection() || !this.isInputDropdown()) return;

    const filtered = this.filteredOptions();
    const isTyping = this.inputValue().trim() !== '' && this.inputValue() !== this.selectedOption()?.text;

    if (filtered.length === 1 && isTyping) {
      this.selectOption(filtered[0]);
      this.dropdownRef()?.close();
    }
  }

  private handleFocus(): void {
    if (this.isInputDropdown()) {
      this.inputRef()?.nativeElement.focus();
    } else {
      this.buttonRef()?.nativeElement.focus();
    }
  }

  private computeSelectedOption(): FilterOption<T> | null {
    const LINE_BREAKS_REGEX = /\r?\n|\r/g;
    const options = this.isInputDropdown() ? this.filteredOptions() : this.optionsList();
    return options.find(option => option.text.replace(LINE_BREAKS_REGEX, '') === this.inputValue().replace(LINE_BREAKS_REGEX, ''));
  }

  private computeDefaultOption(): FilterOption<T> | null {
    if (this.isDefaultOptionFirst()) {
      return this.options()[0];
    }
    return this.isDefaultOptionAll() ? (ALL_OPTION as FilterOption<T>) : null;
  }

  private computeFilteredOptions(): FilterOption<T>[] {
    const filter = this.inputValue().toLowerCase().trim();
    return filter ? this.options().filter(option => option.text.toLowerCase().includes(filter)) : this.options();
  }

  private computeOptionsList(): FilterOption<T>[] {
    const baseOptions = this.isInputDropdown() ? this.filteredOptions() : this.options();
    if (this.withDefaultOption() === 'all') {
      return [ALL_OPTION as FilterOption<T>, ...baseOptions];
    }
    return baseOptions;
  }

  /**
   * ControlValueAccessor methods, theese methods are required for the component
   * to work with ngModel and formControl and should not be removed
   */

  writeValue(value: FilterOption<T> | null): void {
    this.storedSelectedOption.set(value || this.defaultOption());
    this.inputValue.set(value?.text || this.defaultOption()?.text || '');
  }

  registerOnChange(fn: (value: FilterOption<T> | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /* Disables the rule for the file because CVA requires these methods */
  /* eslint-disable @typescript-eslint/no-empty-function */
  private onChange: (value: FilterOption<T> | null) => void = () => {};
  private onTouched: () => void = () => {};
  protected onBlur: () => void = () => {};
  protected onFocus: () => void = () => this.inputValue.set('');
}
